import React, { createContext, useContext, useEffect, useRef, useCallback, useState, type ReactNode } from 'react';
import { useWindows } from './WindowContext';
import { parseRoute, buildRoute, hasRoute } from '../utils/routing';
import type { AppType } from '../types';

interface RoutingContextType {
  /** Get the initial sub-path for an app (used on mount for deep links) */
  getInitialSubPath: (appType: AppType) => string | null;
  /** Update the sub-path for the currently active app (called by app components on internal navigation) */
  setSubPath: (appType: AppType, subPath: string | null) => void;
  /** Whether the initial URL had a route (used to skip boot) */
  hasInitialRoute: boolean;
}

const RoutingContext = createContext<RoutingContextType | null>(null);

interface RoutingProviderProps {
  children: ReactNode;
}

export const RoutingProvider: React.FC<RoutingProviderProps> = ({ children }) => {
  const { windows, openWindow, focusWindow } = useWindows();

  // Parse the initial URL once on mount
  const initialRoute = useRef(parseRoute(window.location.pathname));
  const [hasInitialRoute] = useState(() => hasRoute(window.location.pathname));

  // Track sub-paths per app type
  const subPaths = useRef<Map<AppType, string | null>>(new Map());

  // Track whether initial route has been applied
  const initialRouteApplied = useRef(false);

  // Track whether we're handling a popstate (to avoid pushing state during popstate handling)
  const isHandlingPopstate = useRef(false);

  // Track the last URL we pushed to avoid duplicate pushes
  const lastPushedUrl = useRef(window.location.pathname);

  // Initialize sub-path from URL
  if (initialRoute.current.appType && initialRoute.current.subPath) {
    subPaths.current.set(initialRoute.current.appType, initialRoute.current.subPath);
  }

  // Open the initial window from URL (once WindowContext is ready)
  useEffect(() => {
    if (initialRouteApplied.current) return;
    if (!initialRoute.current.appType) return;

    initialRouteApplied.current = true;
    openWindow(initialRoute.current.appType);
  }, [openWindow]);

  // Sync URL when active window changes
  useEffect(() => {
    if (isHandlingPopstate.current) return;

    const activeWindow = windows.find(w => w.isActive);
    if (!activeWindow) {
      // No active window - set URL to root
      const url = buildRoute(null);
      if (lastPushedUrl.current !== url) {
        lastPushedUrl.current = url;
        window.history.pushState({ appType: null }, '', url);
      }
      return;
    }

    const appType = activeWindow.component;
    const subPath = subPaths.current.get(appType) || null;
    const url = buildRoute(appType, subPath);

    if (lastPushedUrl.current !== url) {
      lastPushedUrl.current = url;
      window.history.pushState({ appType, subPath }, '', url);
    }
  }, [windows]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopstate = () => {
      isHandlingPopstate.current = true;
      const route = parseRoute(window.location.pathname);
      lastPushedUrl.current = window.location.pathname;

      if (route.appType) {
        // Update stored sub-path
        subPaths.current.set(route.appType, route.subPath);

        // Check if window is already open
        const existingWindow = windows.find(w => w.component === route.appType);
        if (existingWindow) {
          focusWindow(existingWindow.id);
        } else {
          openWindow(route.appType);
        }
      }

      // Use setTimeout to reset the flag after the state update
      setTimeout(() => {
        isHandlingPopstate.current = false;
      }, 0);
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [windows, openWindow, focusWindow]);

  const getInitialSubPath = useCallback((appType: AppType): string | null => {
    return subPaths.current.get(appType) || null;
  }, []);

  // Use a ref to track windows for setSubPath so the callback identity is stable
  const windowsRef = useRef(windows);
  windowsRef.current = windows;

  const setSubPath = useCallback((appType: AppType, subPath: string | null) => {
    subPaths.current.set(appType, subPath);

    // Update URL if this app is currently active
    const activeWindow = windowsRef.current.find(w => w.isActive);
    if (activeWindow && activeWindow.component === appType) {
      const url = buildRoute(appType, subPath);
      if (lastPushedUrl.current !== url) {
        lastPushedUrl.current = url;
        window.history.pushState({ appType, subPath }, '', url);
      }
    }
  }, []);

  return (
    <RoutingContext.Provider value={{ getInitialSubPath, setSubPath, hasInitialRoute }}>
      {children}
    </RoutingContext.Provider>
  );
};

export const useRouting = (): RoutingContextType => {
  const context = useContext(RoutingContext);
  if (!context) {
    throw new Error('useRouting must be used within a RoutingProvider');
  }
  return context;
};
